/* eslint-disable max-len */
import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from 'src/app/models/users.model';
import { NbToastrService } from '@nebular/theme';
import { COUNTRY_OPTIONS, CountryOption } from 'src/app/shared/constants';
import { Email } from 'src/app/models/email.model';
import { emailSignature } from 'src/app/shared/emails/signature';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { UserService } from 'src/app/shared/services/user.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  loading = false;
  currentYear = new Date().getFullYear();
  countryOptions = COUNTRY_OPTIONS;

  formData: {
    email: string;
    name: string;
    companyName: string;
    country: CountryOption;
    mobile: string;
    password: string;
    termsAndConditions: boolean;
  } = {
    email: null,
    name: null,
    companyName: null,
    country: this.countryOptions[0],
    mobile: null,
    password: null,
    termsAndConditions: null,
  };

  constructor(
    private toastr: NbToastrService,
    private router: Router,
    private http: HttpClient,
    private userService: UserService,
  ) {}

  ngOnInit() {}

  async onSubmit(form: NgForm) {
    this.loading = true;

    await this.validateForm(form);
    if (form.invalid) {
      this.loading = false;
      return;
    }

    try {
      const user = await this.userService.createUser({
        email: this.formData.email,
        name: this.formData.name,
        companyName: this.formData.companyName,
        mobile: this.formData.mobile,
        countryCode: this.formData.country.code,
        agreedToTerms: this.formData.termsAndConditions,
        password: this.formData.password,
      });

      this.sendRegisterEmail(user);
      this.router.navigate(['/home/convert']);
    } catch (error) {
      this.toastr.warning(error.message, 'Registration Failed');
      console.error(error);
    }

    this.loading = false;
  }

  async validateForm(form: NgForm) {
    if (this.formData.termsAndConditions !== true) {
      form.controls.termsAndConditions.setErrors({ invalid: true });
    }
  }

  private async sendRegisterEmail(user: User) {
    const email: Email = {
      from: {
        email: 'noreply@swiftcsv.com',
        name: 'Swift CSV',
      },
      to: user.email,
      bcc: environment.production ? 'backoffice@swiftcsv.com' : '',
      subject: 'Welcome to Swift CSV',
      html: `
      Good day ${user.name.split(' ')[0]},
      <br /><br />
      Thank you for joining the Swift CSV community where you can convert your PDF bank statements to balanced CSV and XLSX files in minutes.
      <br /><br />
      Swift CSV is designed to convert original PDF bank statements that are sent via email or downloaded from banking applications to balanced .csv and .xlsx files.
      <br /><br />
      Individual File size is limited to under 20Mb and a maximum of 80 pages.
      <br /><br />
      To start click on <b>Convert</b>, then on <b>Enter bank name</b>, you can either select the bank from the drop-down list or enter the name.
      <br /><br />
      Then select the bank statements to load from your computer and the number of credits required will be displayed.
      <br /><br />
      If more credits are required than you have available, there is an option to purchase additional credits by credit card or EFT  before proceeding.
      <br /><br />
      If your bank is not listed, use your country’s <b>All Banks</b> to load the file, we will then create a new conversion model and once completed, convert the statement.
      <br /><br />
      To continue just click on <b>Next</b> to start the conversion process, the credits required will be deducted from your credits balance and the conversion process will start.
      <br /><br />
      The conversion will take a couple of minutes to complete.
      <br /><br />
      Conversion feedback is based on colour:
      <br />
      <ul>
        <li>
          <span style="background: #c1f7d3">Green</span> – the .csv and .xlsx files can be used as the all transactions are in balance, but do check the date before importing
        </li>
        <li>
          <span style="background: #ffd5bd">Orange</span> – some errors were detected, usually due to quality issues, or if the statement did not have a 1st page or a running balance. Our Back Office will correct the file and load the balanced .csv and .xlsx files on your dashboard.
        </li>
        <li>
          <span style="background: #ffbdc5">Red</span> – the conversion failed, we will attempt to correct, but if unable to, the credits used will be refunded.
        </li>
      </ul>
      <br />
      If you want to see what caused a failed conversion click on the “…” on the right of the file, the details, including the bank selected, will be displayed indicating the lines where errors occurred, so unless the incorrect bank was selected, please do not load the same file again as it will have the exact same result.
      <br /><br />
      Swift CSV uses the first page to determine the statement format and then select the conversion model based on that. On some statements we use it to obtain the year of the date, and without the full date the statements will not convert to a green status.
      <br /><br />
      We created the bulk upload facility to negate combining statements as you can upload up to 50 individual statements of the same bank, regardless of the format, and bulk download them as a single CSV and XLSX file, or as individual files if you prefer.
      <br /><br />
      Each print, scan and combine impacts on the quality of the result and is often the difference between a green and an orange or red conversion.
      <br /><br />
      Please note that if you do combine files, the files must be in date sequence from the oldest to the newest and take care that all the statements are exactly the same format as the rest of the statement pages will fail once the format changes.
      <br /><br />
      If you reprint or scan statements please take the following in mind:
      <br />
      <ol>
        <li>The document must be scanned or printed from the original statement produced by the bank</li>
        <li>It must be scanned or printed to the original size</li>
        <li>It must be scanned or printed to the highest quality settings</li>
        <li>There must be no marks applied by outside parties on the statement</li>
        <li>All the pages of the document must be scanned or printed straight</li>
      </ol>
      <br />
      Original PDF statements sent by banks are often protected by a security layer that must be removed before Swift CSV will be able to convert the statement using the following procedure.
      <br />
      <ol>
        <li>Save the file in a folder for locked files</li>
        <li>Right-click on the saved file and select the <b>Open with</b> feature</li>
        <li>Select <b>Google Chrome</b> and enter the password if required</li>
        <li>Print the opened file with <b>Microsoft Print to PDF</b>, or any other quality PDF printer, and do not alter any of the settings</li>
        <li>You can select the pages required but we do need page 1 on all statements where page 1 contains valid transactions</li>
        <li>Save the opened file in a different folder adding an indication to the file name that the file is now opened</li>
        <li>Upload the opened file to Swift CSV</li>
      </ol>
      <br />
      You are most welcome to contact us if you require additional information.
      <br /><br />
      ${emailSignature}
      `,
    };

    this.http
      .post(`${environment.functionsRootUrl}/email-sendEmail/send`, email)
      .toPromise()
      .then((success) => {
        console.log('Success Sending Email: ', success);
      })
      .catch((error) => {
        console.warn('Failed Sending Email: ', error);
      });
  }
}
